/**
 * Wire-payload → IncomingOcppEvent dispatcher.
 *
 * Wire-boundary safety (BUG-001 fix):
 *   The brand constructors (`TransactionId.of` etc.) throw `RangeError`
 *   when handed garbage like `transactionId=-1`. Throwing here would
 *   bubble unhandled through CallDispatcher and surface as a sticky
 *   Navbar `lastError`. evse2 was permissive at this boundary — it
 *   replied Rejected after a tx-lookup miss.
 *
 *   We compromise: we still REJECT the request (per OCPP 1.6J §5.13)
 *   but as a structured `InvalidIncoming` event so the handler can
 *   reply CALLRESULT `{ status: "Rejected" }` and the Event Log shows
 *   a clean rejection rather than a thrown error.
 */
import { ChargingProfileId, ConnectorId, TransactionId } from '@/domain/types';
import type {
  IncomingOcppEvent,
  InvalidIncoming,
} from '@/protocol/OcppAdapter';

type IdFactory<T> = { of(raw: number): T };

function tryParseId<T>(
  factory: IdFactory<T>,
  raw: unknown,
): { ok: true; value: T } | { ok: false; reason: string } {
  if (typeof raw !== 'number') {
    return { ok: false, reason: `expected number, got ${typeof raw}` };
  }
  try {
    return { ok: true, value: factory.of(raw) };
  } catch (e) {
    return { ok: false, reason: e instanceof Error ? e.message : String(e) };
  }
}

function invalid(
  messageId: string,
  action: string,
  reason: string,
  rawPayload: Record<string, unknown>,
): InvalidIncoming {
  return {
    kind: 'Invalid',
    messageId,
    action: 'Invalid',
    originalAction: action,
    reason,
    rawPayload,
  };
}

/** Map an OCPP 1.6 CALL frame's (action, payload) into a domain-level event. */
export function dispatchIncoming(
  messageId: string,
  action: string,
  payload: Record<string, unknown>,
): IncomingOcppEvent {
  switch (action) {
    case 'Reset':
      return {
        kind: 'Reset',
        messageId,
        action,
        type: payload['type'] as 'Hard' | 'Soft',
      };
    case 'RemoteStartTransaction': {
      // connectorId is optional; only validate if present.
      if (payload['connectorId'] !== undefined) {
        const r = tryParseId(ConnectorId, payload['connectorId']);
        if (!r.ok) {
          return invalid(messageId, action, `connectorId: ${r.reason}`, payload);
        }
        return {
          kind: 'RemoteStartTransaction',
          messageId,
          action,
          idTag: payload['idTag'] as string,
          connectorId: r.value,
        };
      }
      return {
        kind: 'RemoteStartTransaction',
        messageId,
        action,
        idTag: payload['idTag'] as string,
      };
    }
    case 'RemoteStopTransaction': {
      const r = tryParseId(TransactionId, payload['transactionId']);
      if (!r.ok) {
        return invalid(messageId, action, `transactionId: ${r.reason}`, payload);
      }
      return {
        kind: 'RemoteStopTransaction',
        messageId,
        action,
        transactionId: r.value,
      };
    }
    case 'GetConfiguration':
      return {
        kind: 'GetConfiguration',
        messageId,
        action,
        ...(Array.isArray(payload['key']) ? { keys: payload['key'] as string[] } : {}),
      };
    case 'ChangeConfiguration':
      return {
        kind: 'ChangeConfiguration',
        messageId,
        action,
        key: payload['key'] as string,
        value: payload['value'] as string,
      };
    case 'ChangeAvailability': {
      // connectorId here is 0 OR a positive integer (0 = whole station).
      const raw = payload['connectorId'];
      if (typeof raw !== 'number' || !Number.isInteger(raw) || raw < 0) {
        return invalid(messageId, action, `connectorId: out of range`, payload);
      }
      const connectorId = raw === 0 ? 0 : ConnectorId.of(raw);
      return {
        kind: 'ChangeAvailability',
        messageId,
        action,
        connectorId,
        type: payload['type'] as 'Operative' | 'Inoperative',
      };
    }
    case 'TriggerMessage': {
      if (payload['connectorId'] !== undefined) {
        const r = tryParseId(ConnectorId, payload['connectorId']);
        if (!r.ok) {
          return invalid(messageId, action, `connectorId: ${r.reason}`, payload);
        }
        return {
          kind: 'TriggerMessage',
          messageId,
          action,
          requestedMessage: payload['requestedMessage'] as string,
          connectorId: r.value,
        };
      }
      return {
        kind: 'TriggerMessage',
        messageId,
        action,
        requestedMessage: payload['requestedMessage'] as string,
      };
    }
    case 'SetChargingProfile': {
      const raw = payload['connectorId'];
      if (typeof raw !== 'number' || !Number.isInteger(raw) || raw < 0) {
        return invalid(messageId, action, `connectorId: out of range`, payload);
      }
      const connectorId = raw === 0 ? 0 : ConnectorId.of(raw);
      return {
        kind: 'SetChargingProfile',
        messageId,
        action,
        connectorId,
        csChargingProfile: (payload['csChargingProfiles'] ??
          payload['csChargingProfile'] ??
          {}) as Record<string, unknown>,
      };
    }
    case 'ClearChargingProfile': {
      // All fields optional, but if PRESENT and invalid → Rejected.
      const out: {
        kind: 'ClearChargingProfile';
        messageId: string;
        action: string;
        id?: ChargingProfileId;
        connectorId?: ConnectorId;
        chargingProfilePurpose?: string;
        stackLevel?: number;
      } = { kind: 'ClearChargingProfile', messageId, action };
      if (payload['id'] !== undefined) {
        const r = tryParseId(ChargingProfileId, payload['id']);
        if (!r.ok) return invalid(messageId, action, `id: ${r.reason}`, payload);
        out.id = r.value;
      }
      if (payload['connectorId'] !== undefined) {
        const r = tryParseId(ConnectorId, payload['connectorId']);
        if (!r.ok) return invalid(messageId, action, `connectorId: ${r.reason}`, payload);
        out.connectorId = r.value;
      }
      if (typeof payload['chargingProfilePurpose'] === 'string') {
        out.chargingProfilePurpose = payload['chargingProfilePurpose'];
      }
      if (typeof payload['stackLevel'] === 'number') {
        out.stackLevel = payload['stackLevel'];
      }
      return out;
    }
    case 'GetCompositeSchedule': {
      const r = tryParseId(ConnectorId, payload['connectorId']);
      if (!r.ok) return invalid(messageId, action, `connectorId: ${r.reason}`, payload);
      return {
        kind: 'GetCompositeSchedule',
        messageId,
        action,
        connectorId: r.value,
        duration: payload['duration'] as number,
        ...(typeof payload['chargingRateUnit'] === 'string'
          ? { chargingRateUnit: payload['chargingRateUnit'] as 'A' | 'W' }
          : {}),
      };
    }
    case 'UnlockConnector': {
      const r = tryParseId(ConnectorId, payload['connectorId']);
      if (!r.ok) return invalid(messageId, action, `connectorId: ${r.reason}`, payload);
      return {
        kind: 'UnlockConnector',
        messageId,
        action,
        connectorId: r.value,
      };
    }
    case 'DataTransfer':
      return {
        kind: 'DataTransfer',
        messageId,
        action,
        vendorId: payload['vendorId'] as string,
        ...(typeof payload['messageId'] === 'string'
          ? { payloadMessageId: payload['messageId'] }
          : {}),
        ...(typeof payload['data'] === 'string' ? { data: payload['data'] } : {}),
      };
    case 'GetLocalListVersion':
      return { kind: 'GetLocalListVersion', messageId, action };
    case 'ClearCache':
      // BUG-028 — OCPP 1.6J §5.2 ClearCache: empty request payload by spec
      // (Ajv schema requires {} — no fields). Surfacing as a dedicated
      // event so handleCsmsCall can wipe the per-station AuthCache via
      // the store's `clearAuthCache()` action. Spec mandates LocalAuthList
      // is NOT touched — those rows are managed by SendLocalList only.
      return { kind: 'ClearCache', messageId, action };
    case 'SendLocalList': {
      const lv = payload['listVersion'];
      if (typeof lv !== 'number' || !Number.isInteger(lv) || lv < 0) {
        return invalid(messageId, action, 'listVersion must be a non-negative integer', payload);
      }
      const ut = payload['updateType'];
      if (ut !== 'Full' && ut !== 'Differential') {
        return invalid(messageId, action, `updateType must be Full or Differential, got ${String(ut)}`, payload);
      }
      const list = payload['localAuthorizationList'];
      const arr =
        Array.isArray(list)
          ? (list as Record<string, unknown>[])
          : ([] as Record<string, unknown>[]);
      return {
        kind: 'SendLocalList',
        messageId,
        action,
        listVersion: lv,
        updateType: ut,
        localAuthorizationList: arr,
      };
    }
    case 'UpdateFirmware': {
      // Session 20 — OCPP 1.6J §4.x FirmwareManagement. location + retrieveDate.
      const location = payload['location'];
      if (typeof location !== 'string') {
        return invalid(messageId, action, 'location must be a string', payload);
      }
      return {
        kind: 'UpdateFirmware',
        messageId,
        action,
        location,
        ...(typeof payload['retrieveDate'] === 'string'
          ? { retrieveDate: payload['retrieveDate'] }
          : {}),
      };
    }
    case 'GetDiagnostics': {
      const location = payload['location'];
      if (typeof location !== 'string') {
        return invalid(messageId, action, 'location must be a string', payload);
      }
      return {
        kind: 'GetDiagnostics',
        messageId,
        action,
        location,
        ...(typeof payload['startTime'] === 'string' ? { startTime: payload['startTime'] } : {}),
        ...(typeof payload['stopTime'] === 'string' ? { stopTime: payload['stopTime'] } : {}),
      };
    }
    case 'ReserveNow': {
      // Session 21 — OCPP 1.6J §3.x Reservation.
      const r = tryParseId(ConnectorId, payload['connectorId']);
      if (!r.ok) return invalid(messageId, action, `connectorId: ${r.reason}`, payload);
      const reservationId = payload['reservationId'];
      const idTag = payload['idTag'];
      const expiryDate = payload['expiryDate'];
      if (typeof reservationId !== 'number' || !Number.isInteger(reservationId)) {
        return invalid(messageId, action, 'reservationId must be an integer', payload);
      }
      if (typeof idTag !== 'string' || typeof expiryDate !== 'string') {
        return invalid(messageId, action, 'idTag and expiryDate must be strings', payload);
      }
      return { kind: 'ReserveNow', messageId, action, reservationId, connectorId: r.value, idTag, expiryDate };
    }
    case 'CancelReservation': {
      const reservationId = payload['reservationId'];
      if (typeof reservationId !== 'number' || !Number.isInteger(reservationId)) {
        return invalid(messageId, action, 'reservationId must be an integer', payload);
      }
      return { kind: 'CancelReservation', messageId, action, reservationId };
    }
    default:
      return { kind: 'Unknown', messageId, action, rawPayload: payload };
  }
}
